import { Box, Center, Spinner, useToast } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router";

import { APPROVED, PENDING, REJECTED, SUBMITTED } from "@equidefi/constants/accreditation";
import { EquidefiMarkdown } from "@equidefi/portals/components/Markdown";
import { AccreditationUploadSection } from "@equidefi/portals/components/accreditation/AccreditationUploadSection";
import { ViewAccreditationFiles } from "@equidefi/portals/components/accreditation/ViewAccreditationFiles";
import { useGetAccreditationFiles, useUpdateVault } from "@equidefi/portals/hooks/useAccreditation";
import { BannerAlert, Heading, Text } from "@equidefi/ui";

import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import { VAULT_ROUTES } from "@/constants/routes";
import { useInvestment } from "@/hooks/useInvestments";
import { InvestmentTitleCard, VaultPageHeader } from "@/pages/app/vault/v2/InvestmentDetails/components";
import { VaultContainer } from "@/pages/app/vault/v2/components";

const REFETCH_INTERVAL = 30_000;

const bannerConfigMap = {
  [APPROVED]: {
    status: "success",
    title: "Accreditation Approved",
    text: "The documents you submitted have been approved and your accreditation has been verified.",
  },
  [SUBMITTED]: {
    status: "info",
    title: "Accreditation Submitted",
    text: "Your submitted verification documents are awaiting review.",
  },
  [PENDING]: {
    status: "warning",
    title: "Accreditation Pending",
    text: "You have not yet completed the verification process.",
  },
  [REJECTED]: {
    status: "error",
    title: "Accreditation Rejected",
    text: "The documents you submitted have been rejected.",
  },
};

const AccreditationPage = () => {
  const { id } = useParams();
  const {
    data: investment,
    isFetchedAfterMount,
    refetch: refetchInvestment,
  } = useInvestment(id, {
    refetchOnMount: true,
  });
  const toast = useToast();

  const { vault, offering } = investment ?? {};
  const bannerConfig = bannerConfigMap[vault?.status] ?? {};

  const {
    data: accreditationFiles,
    refetch: refetchFiles,
    isFetching,
  } = useGetAccreditationFiles(vault?.id, {
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: vault?.plaid_auth ? REFETCH_INTERVAL : false,
  });
  const updateVault = useUpdateVault(investment?.id);

  const refetch = useCallback(async () => {
    await refetchFiles();
    await refetchInvestment();
  }, [refetchFiles, refetchInvestment]);

  const canSubmit = useMemo(
    () =>
      !isFetching &&
      vault?.status !== SUBMITTED &&
      accreditationFiles &&
      accreditationFiles?.length > 0 &&
      vault?.utility_bill,
    [accreditationFiles, vault?.status, vault?.utility_bill, isFetching],
  );

  const onSubmit = async () => {
    try {
      await updateVault.mutateAsync({
        submitted: true,
        skip_transition: investment.next !== "ACCREDITATION",
      });
      await refetch();
    } catch (error) {
      console.log(error);
      toast({
        status: "error",
        description: "Failed to submit your files.",
      });
    }
  };

  if (!isFetchedAfterMount) {
    return (
      <Center h="100vh">
        <Spinner />
      </Center>
    );
  }

  const issuer = investment?.issuer;

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <VaultPageHeader
        crumbs={[
          { label: "Portfolio", to: "/vault/dashboard" },
          { label: issuer?.name ?? "Issuer", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: offering?.name ?? "Offering", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: "Investment", to: VAULT_ROUTES.investmentDetails(investment?.id) },
          { label: "Accreditation" },
        ]}
        backTo={VAULT_ROUTES.investmentDetails(investment?.id)}
        backLabel="Back To Investment"
      />

      <InvestmentTitleCard investment={investment} issuer={issuer} offering={offering} />

      <Box bg="white" borderRadius="md" boxShadow="md" p="4" mt="4">
        <Heading fontWeight="bold" as="h1" mb="2">
          Accreditation Verification
        </Heading>
        <Text textStyle="body2" mb="4">
          You must prove that you're an accredited investor to participate in this offering.
        </Text>

        <BannerAlert status={bannerConfig.status} title={bannerConfig.title} description={bannerConfig.text} />

        {vault?.rejection_reason && vault?.status === REJECTED && (
          <Box bgColor="gray.100" borderColor="gray.300" border="1px solid" p="4" borderRadius="8px" mt="4">
            <Heading textStyle="context" color="black">
              REVIEW NOTES
            </Heading>
            <EquidefiMarkdown>{vault.rejection_reason}</EquidefiMarkdown>
          </Box>
        )}
      </Box>

      {vault?.status !== APPROVED ? (
        <>
          <AccreditationUploadSection
            vault={vault}
            investment={investment}
            offering={offering}
            accreditationFiles={accreditationFiles}
            refetch={refetch}
          />
          <WorkflowButton onClick={onSubmit} isDisabled={!canSubmit}>
            Submit
          </WorkflowButton>
        </>
      ) : (
        <ViewAccreditationFiles
          vault={vault}
          investment={investment}
          offering={offering}
          accreditationFiles={accreditationFiles}
        />
      )}
    </VaultContainer>
  );
};

export default AccreditationPage;
